import React, { useEffect, useMemo } from 'react';
import { Table, Card, Button, Row, Col, ProgressBar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmailStats } from '../features/logSlice';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdminLayout from '../components/AdminLayout';

const EmailStatsPage = () => {
  const dispatch = useDispatch();
  const token = useSelector(state => state.auth.token);
  const { stats } = useSelector(state => state.logs);

  useEffect(() => {
    dispatch(fetchEmailStats(token));
  }, [dispatch, token]);

  const totals = useMemo(() => {
    const totalChats = (stats || []).reduce((sum, item) => sum + Number(item.total || 0), 0);
    const uniqueEmails = (stats || []).length;
    const avg = uniqueEmails ? (totalChats / uniqueEmails).toFixed(1) : 0;
    const highest = (stats || []).reduce((max, item) => Math.max(max, Number(item.total || 0)), 0);
    return { totalChats, uniqueEmails, avg, highest };
  }, [stats]);

  const exportCSV = () => {
    const csv = [
      ['Email', 'Total Chats'],
      ...stats.map(item => [item.email, item.total])
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'email_stats.csv');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [['Email', 'Total Chats']],
      body: stats.map(item => [item.email, item.total]),
    });
    doc.save('email_stats.pdf');
  };

  return (
    <AdminLayout>
      <h4>Email Usage Analytics</h4>

      <Row className="g-3 mt-1">
        <Col md={3}>
          <Card className="admin-stat-card">
            <div className="text-muted small">Total Chat Events</div>
            <h3 className="mb-0">{totals.totalChats}</h3>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="admin-stat-card">
            <div className="text-muted small">Unique Emails</div>
            <h3 className="mb-0">{totals.uniqueEmails}</h3>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="admin-stat-card">
            <div className="text-muted small">Avg Chats / Email</div>
            <h3 className="mb-0">{totals.avg}</h3>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="admin-stat-card">
            <div className="text-muted small">Top Email Volume</div>
            <h3 className="mb-0">{totals.highest}</h3>
          </Card>
        </Col>
      </Row>

      <Card className="p-3 shadow-sm mt-3">
        <div className="text-end mb-3">
          <Button size="sm" className="me-2" onClick={exportCSV}>Export CSV</Button>
          <Button size="sm" variant="dark" onClick={exportPDF}>Export PDF</Button>
        </div>

        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th>Total Chats</th>
            </tr>
          </thead>
          <tbody>
            {stats.length === 0 ? (
              <tr>
                <td colSpan="2" className="text-center">No email stats yet.</td>
              </tr>
            ) : (
              stats.map((item, i) => (
                <tr key={i}>
                  <td>{item.email}</td>
                  <td style={{ minWidth: 180 }}>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ minWidth: 35 }}>{item.total}</div>
                      <div className="flex-grow-1">
                        <ProgressBar
                          now={totals.highest ? (Number(item.total || 0) / totals.highest) * 100 : 0}
                          variant="info"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
    </AdminLayout>
  );
};

export default EmailStatsPage;
